'use client';

import { Pagination as AntPagination } from 'antd';
import cn from 'classnames';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React from 'react';

import { ReactComponent as ArrowBackwardCircle } from '@/app/_icons/ArrowBackwardCircleDark.svg';
import { ReactComponent as ArrowForwardCircle } from '@/app/_icons/ArrowForwardCircleDark.svg';
import { PaginationItemsMeta } from '@/types/pagination/pagination.types';

import styles from './pagination.module.scss';

export const Pagination: React.FC<{
  onPageChange?: (page: number, pageSize: number) => void;
  itemsMeta: PaginationItemsMeta;
  pageSize: number;
}> = ({ onPageChange, itemsMeta, pageSize }) => {
  const searchParams = useSearchParams();
  const currentPage = itemsMeta.currentPage || 1;
  const totalPages = Math.ceil(itemsMeta.filterCount / pageSize);

  function getHref(page: number) {
    const newSearchParams = new URLSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });

    return `?${newSearchParams.toString()}`;
  }

  return (
    <div className={cn(styles.resultsPagination, 'results-pagination')}>
      <AntPagination
        size="default"
        total={itemsMeta.filterCount}
        itemRender={(page, type, el) => {
          if (type === 'prev') {
            return (
              <Link
                onClick={onPageChange ? (e) => e.preventDefault() : undefined}
                href={getHref(page)}
                className={styles.link}
                aria-label="prev page"
                prefetch={false}
              >
                <ArrowBackwardCircle />
              </Link>
            );
          }
          if (type === 'next') {
            return (
              <Link
                href={getHref(page)}
                onClick={onPageChange ? (e) => e.preventDefault() : undefined}
                className={styles.link}
                aria-label="next page"
                prefetch={false}
              >
                <ArrowForwardCircle />
              </Link>
            );
          }
          if (type === 'page') {
            return (
              <Link
                href={getHref(page)}
                onClick={onPageChange ? (e) => e.preventDefault() : undefined}
                className={styles.link}
                prefetch={false}
              >
                {page}
              </Link>
            );
          }
          if (type === 'jump-next') {
            const jumpPage = Math.min(currentPage + 5, totalPages);

            return (
              <Link
                href={getHref(jumpPage)}
                onClick={onPageChange ? (e) => e.preventDefault() : undefined}
                className={styles.link}
                aria-label="jump to next pages"
                legacyBehavior
                prefetch={false}
              >
                {el}
              </Link>
            );
          }
          if (type === 'jump-prev') {
            const jumpPage = Math.max(currentPage - 5, 1);
            
            return (
              <Link
                href={getHref(jumpPage)}
                onClick={onPageChange ? (e) => e.preventDefault() : undefined}
                className={styles.link}
                aria-label="jump to previous pages"
                legacyBehavior
                prefetch={false}
              >
                {el}
              </Link>
            );
          }
          return el;
        }}
        pageSize={pageSize}
        showSizeChanger={false}
        onChange={onPageChange ?? undefined}
        current={currentPage}
      />
    </div>
  );
};
